import Uri from 'jsuri';
import path from 'path';

import { LABS_HOSTNAME } from 'bundles/labs-common/constants/LabUrls';
import type { FrameMessage, UrlFrameMessage } from 'bundles/labs-common/types/FramedLab';
import type { LaunchUrlParams } from 'bundles/labs-common/types/LabLaunch';

// If we get a parameter in the launch URL from the backend, allow us to define an override
// Uses `override` if it exists
// Otherwise it just converts the response value from string to boolean
export const handleLaunchUrlParamOverride = (
  response: string | null,
  override?: boolean | string
): boolean | string | undefined => {
  if (typeof override !== 'undefined') {
    return override;
  } else if (response === 'true') {
    return true;
  } else if (response === 'false') {
    return false;
  } else {
    return undefined;
  }
};

// Reconstruct a URL, but allow params to be overriden via launchUrlParams
export const constructLaunchUrlQueryString = (launchUrl: string, launchUrlParams?: LaunchUrlParams): string => {
  const params = new Uri(launchUrl);

  // Support anything.
  // eslint-disable-next-line guard-for-in
  for (const key in launchUrlParams) {
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    const idealValue = handleLaunchUrlParamOverride(params.getQueryParamValue(key), launchUrlParams?.[key]);
    if (idealValue === undefined) {
      params.deleteQueryParam(key);
    } else {
      params.replaceQueryParam(key, String(idealValue));
    }
  }

  return params.query();
};

// Builds a /connect/workspaceId URL using the specified workspaceGatewayHost and any params.
export const constructLaunchUrl = (
  launchUrl: string,
  launchUrlParams?: LaunchUrlParams,
  workspaceGatewayHost?: string,
  workspaceId?: string
): string => {
  const queryString = constructLaunchUrlQueryString(launchUrl, launchUrlParams);

  if (workspaceGatewayHost && workspaceId) {
    return new Uri()
      .setProtocol('https')
      .setHost(workspaceGatewayHost)
      .setPath(path.join('connect', workspaceId))
      .setQuery(queryString)
      .toString();
  } else {
    return new Uri(launchUrl).setQuery(queryString).toString();
  }
};

// Just checks new URL() for an exception.
export const isUrlParsable = (urlToCheck: string): boolean => {
  try {
    // Note: This truthy check is for avoiding the "Do not use 'new' for side effects" ESLint error.
    return !!new URL(urlToCheck);
  } catch (e) {
    return false;
  }
};

export const constructWorkspaceUrl = (workspaceId: string, hostname: string = LABS_HOSTNAME): string => {
  return `https://${hostname}/connect/${workspaceId}`;
};

// Extracts the hostname from a long URL
// It's simply new URL(url).hostname, but with checks.
// Example
// input: https://12345678.labs.coursera.org
// output: 12345678.labs.coursera.org
export const extractLabHostname = (launchUrl?: string): string => {
  if (!launchUrl) {
    return '';
  }

  return isUrlParsable(launchUrl) ? new URL(launchUrl).hostname : launchUrl;
};

// Extracts a path from the outer URL
// Example input: https://12345678.labs.coursera.org/?path=%2Ftree&abc=1
// Example output: /tree
export const extractContentPath = (launchUrl?: string): string => {
  if (!launchUrl) {
    return '';
  }

  return isUrlParsable(launchUrl) ? new URL(launchUrl).searchParams.get('path') || '' : launchUrl;
};

// Example input: https://12345678.labs.coursera.org/?path=%2Ftree&abc=1
// Example output: https://12345678.labs.coursera.org
export const extractUrlOrigin = (currentLabUrl?: string): string => {
  if (!currentLabUrl) {
    return '';
  }

  return isUrlParsable(currentLabUrl) ? new URL(currentLabUrl).origin : currentLabUrl;
};

// Given an inner frame URL, get the entire inner frame path (for the outer frame to render)
// Example input: https://12345678.labs.coursera.org/tree?query=1#hash=2
// Example output: /tree?query=1#hash=2
export const getPathFromUrl = (innerFrameUrl: string): string => {
  try {
    const innerUrl = new URL(innerFrameUrl);
    return innerUrl.pathname + innerUrl.search + innerUrl.hash;
  } catch (error) {
    return innerFrameUrl;
  }
};

// Returns a new hostname for the combination of
// devNameOrHostname: a shorthand dev name or full hostname: Takes inputs like "dev9" or "hub.dev9.labs-dev.coursera.org", or "hub.labs.coursera.org"
// workspaceId: if not present, defaults to `hub`
export const constructOrigin = (devNameOrHostname = LABS_HOSTNAME, workspaceId?: string) => {
  if (devNameOrHostname.startsWith('dev')) {
    // For the dev stack shorthand, we don't mind hard-coding the dev URL.
    // For emergency scenarios, simply avoid specifying the shorthand; specify the full hostname.
    // eslint-disable-next-line no-restricted-syntax
    return `${workspaceId || 'hub'}.${devNameOrHostname}.labs-dev.coursera.org`;
  } else if (workspaceId) {
    // Full hostname was provided, but we need to slide in the workspace id
    // However, it might be a full dev hostname
    // We only expect hub hostnames anyways, and this works for both dev and prod.
    return devNameOrHostname.replace('hub', workspaceId);
  } else {
    // No workspaceId was provided; just use the name as-is
    return devNameOrHostname;
  }
};

export const updateUrlQueryString = (currentUri: Uri, queryKey: string, value?: string): Uri => {
  if (currentUri.hasQueryParam(queryKey)) {
    currentUri.deleteQueryParam(queryKey);
  }

  if (queryKey && value) {
    currentUri.addQueryParam(queryKey, value);
  }

  return currentUri;
};

// This is intended to accept 'any'
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isMessageFromInnerFrame = (messageData: any): messageData is FrameMessage => {
  return typeof messageData === 'object' && messageData.fromInnerFrame === true;
};

// This is intended to accept 'any'
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isInnerFrameMessageAUrl = (messageData: any): messageData is UrlFrameMessage => {
  return isMessageFromInnerFrame(messageData) && 'innerFrameUrl' in messageData;
};

// This is intended to accept 'any'
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isInnerFrameMessageAnIsActive = (messageData: any): messageData is UrlFrameMessage => {
  return isMessageFromInnerFrame(messageData) && 'isActive' in messageData && messageData.isActive === true;
};

// Check if the url starts with protocol, and if it doesn't add "//"
// This will force the browser to resolve it as an absolure url
export const addProtocol = (url: string): string => {
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return `//${url}`;
  }
  return url;
};
